/**
 * Tenant Branding Context
 * Single source of branding for the whole site.
 *
 * Org resolution: signed-in user's org -> subdomain tenant org -> master org.
 * Applies branding as CSS variables consumed by the Tailwind theme tokens
 * (navigate-gold / navigate-dark / navigate-surface / cerebro-accent), fonts,
 * button radius and favicon. Refreshes immediately after an admin saves
 * (window event 'branding-updated') and when the tab regains focus.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { API_BASE } from '../config/api';

const API = API_BASE;
export const MASTER_ORG_ID = 'org_navigate_master';

// Defaults reproduce the current Cerebro look exactly
export const DEFAULT_BRANDING = {
  site_name: 'Cerebro Core',
  name: 'Cerebro Core',
  logo_url: 'https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png',
  favicon_url: null,
  login_background_url: null,
  primary_color: '#C8A85A',
  secondary_color: '#0F1B2D',
  accent_color: '#1EBFB3',
  heading_font: 'Outfit',
  body_font: 'Inter',
  button_style: 'rounded',
  button_radius: '6px',
  header_tagline: 'A Workforce Capability Platform',
  footer_text: '',
  support_email: '',
  customised_fields: [],
  powered_by_visible: true,
};

const DEFAULT_SURFACES = { surface: '#1A2942', surface2: '#243B5C', primaryLight: '#D4B86A' };
const BUNDLED_FONTS = ['Inter', 'Outfit'];

const TenantBrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  loading: true,
  orgId: MASTER_ORG_ID,
  applyBranding: () => {},
  updateBranding: async () => {},
  refreshBranding: async () => {},
  resetToDefault: () => {},
});

function hexToRgb(hex) {
  const h = (hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function mix(hex, withHex, amount) {
  const a = hexToRgb(hex);
  const b = hexToRgb(withHex);
  if (!a || !b) return hex;
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * amount));
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function rgbVar(hex) {
  const rgb = hexToRgb(hex);
  return rgb ? rgb.join(' ') : null;
}

function loadGoogleFont(font) {
  if (!font || BUNDLED_FONTS.includes(font)) return;
  const id = `brand-font-${font.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font).replace(/%20/g, '+')}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

function updateFavicon(url) {
  let link = document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (!link.dataset.defaultHref) link.dataset.defaultHref = link.href || '';
  link.href = url || link.dataset.defaultHref;
}

export function applyBrandingToDocument(b) {
  const root = document.documentElement;
  const custom = new Set(b.customised_fields || []);
  const primary = b.primary_color || DEFAULT_BRANDING.primary_color;
  const secondary = b.secondary_color || DEFAULT_BRANDING.secondary_color;
  const accent = b.accent_color || DEFAULT_BRANDING.accent_color;
  const primaryLight = custom.has('primary_color') ? mix(primary, '#ffffff', 0.12) : DEFAULT_SURFACES.primaryLight;
  const surface = custom.has('secondary_color') ? mix(secondary, '#ffffff', 0.07) : DEFAULT_SURFACES.surface;
  const surface2 = custom.has('secondary_color') ? mix(secondary, '#ffffff', 0.14) : DEFAULT_SURFACES.surface2;

  const vars = {
    '--brand-primary-rgb': rgbVar(primary),
    '--brand-primary-light-rgb': rgbVar(primaryLight),
    '--brand-secondary-rgb': rgbVar(secondary),
    '--brand-surface-rgb': rgbVar(surface),
    '--brand-surface-2-rgb': rgbVar(surface2),
    '--brand-accent-rgb': rgbVar(accent),
    '--brand-primary': primary,
    '--brand-secondary': secondary,
    '--brand-accent': accent,
    '--brand-font-heading': `'${b.heading_font || 'Outfit'}'`,
    '--brand-font-body': `'${b.body_font || 'Inter'}'`,
    '--brand-button-radius': b.button_radius || DEFAULT_BRANDING.button_radius,
    // Legacy variables (kept for any consumers)
    '--tenant-primary': primary,
    '--tenant-primary-light': primaryLight,
    '--tenant-primary-10': `${primary}1A`,
    '--tenant-primary-20': `${primary}33`,
  };
  Object.entries(vars).forEach(([k, v]) => { if (v) root.style.setProperty(k, v); });
  const setOrClear = (name, on, value) => (on ? root.style.setProperty(name, value) : root.style.removeProperty(name));
  setOrClear('--brand-amber-400-rgb', custom.has('primary_color'), rgbVar(mix(primary, '#ffffff', 0.12)));
  setOrClear('--brand-amber-500-rgb', custom.has('primary_color'), rgbVar(primary));
  setOrClear('--brand-teal-400-rgb', custom.has('accent_color'), rgbVar(mix(accent, '#ffffff', 0.12)));
  setOrClear('--brand-teal-500-rgb', custom.has('accent_color'), rgbVar(accent));
  // Legacy #D4AF37 styles (App.css) only switch when a primary colour is customised
  if (custom.has('primary_color')) root.style.setProperty('--brand-gold-legacy', primary);
  else root.style.removeProperty('--brand-gold-legacy');
  loadGoogleFont(b.heading_font);
  loadGoogleFont(b.body_font);
  updateFavicon(b.favicon_url);
}

export function TenantBrandingProvider({ children }) {
  const { user } = useAuth();
  const { tenant } = useTenant() || {};
  const orgId = user?.organization_id || tenant?.orgId || MASTER_ORG_ID;
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const orgRef = useRef(orgId);
  orgRef.current = orgId;

  const refreshBranding = useCallback(async () => {
    const id = orgRef.current;
    try {
      // Cache-busting param so saved changes show without a manual cache clear
      const res = await axios.get(`${API}/api/organizations/${id}/branding`, { params: { _: Date.now() } });
      const data = res.data || {};
      setBranding({ ...DEFAULT_BRANDING, ...data, name: data.site_name || DEFAULT_BRANDING.site_name, powered_by_visible: true });
    } catch (error) {
      setBranding(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshBranding(); }, [orgId, refreshBranding]);

  useEffect(() => {
    const onUpdated = () => refreshBranding();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshBranding(); };
    window.addEventListener('branding-updated', onUpdated);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('branding-updated', onUpdated);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshBranding]);

  useEffect(() => { applyBrandingToDocument(branding); }, [branding]);

  const updateBranding = async (newBranding, targetOrgId) => {
    const id = targetOrgId || orgId;
    const token = localStorage.getItem('token');
    const res = await axios.put(`${API}/api/organizations/${id}/branding`, newBranding, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (id === orgRef.current && res.data?.branding) {
      setBranding({ ...DEFAULT_BRANDING, ...res.data.branding, name: res.data.branding.site_name, powered_by_visible: true });
    }
    window.dispatchEvent(new Event('branding-updated'));
    return { success: true, branding: res.data?.branding };
  };

  const resetToDefault = () => setBranding(DEFAULT_BRANDING);

  return (
    <TenantBrandingContext.Provider value={{
      branding,
      loading,
      orgId,
      applyBranding: applyBrandingToDocument,
      updateBranding,
      refreshBranding,
      resetToDefault,
    }}>
      {children}
    </TenantBrandingContext.Provider>
  );
}

export function useTenantBranding() {
  return useContext(TenantBrandingContext);
}

export default TenantBrandingContext;
